<template>
	<div v-click-outside="closeDropdown" class="user-icon-container">
		<p class="user-icon" @click="toggleDropdown" v-text="initials" />
		<div v-if="showDropdown" class="user-menu">
			<button class="btn btn-none user-menu-item" title="Edit Account" @click="openAccountModal">
				Edit Account
			</button>
			<button class="btn btn-none user-menu-item" title="Logout" @click="logout">
				Logout
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
	import type { HttpHelper } from '../helper/http';
	import { Messages } from '../enums/messages';
	import { computed, inject, ref } from 'vue';
	import { useRouter } from 'vue-router';
	import { useStore } from 'vuex';

	const emit = defineEmits<{
		(e: 'open-account-modal', value: boolean): void,
	}>();

	const http = inject<HttpHelper>('http')!;
	const store = useStore();
	const router = useRouter();
	const showDropdown = ref(false);

	const initials = computed(() => {
		if (typeof store.state.account.name !== 'string') return '--';
		const nameParts: Array<string> = store.state.account.name.split(' ');
		if (nameParts.length >= 2) {
			return `${nameParts[0].substring(0, 1).toUpperCase()}${nameParts[1].substring(0, 1).toUpperCase()}`;
		} else {
			return nameParts[0].substring(0, 2).toUpperCase();
		}
	});

	const closeDropdown = () => {
		showDropdown.value = false;
	};

	const toggleDropdown = () => {
		showDropdown.value = !showDropdown.value;
	};

	const openAccountModal = () => {
		emit('open-account-modal', true);
	};

	const logout = async () => {
		// Backend logout.
		const response: Response = await http.get('/api/logout');
		const result = await response.json().catch(() => ({ redirect: {} }));

		// Frontend logout.
		store.dispatch('account/logout', 'manual');
		store.dispatch('notification/add', {
			type: 'success',
			message: Messages.LOGOUT_SUCCESS,
		});
		window.sessionStorage.removeItem('lc:login_allow_token');
		window.sessionStorage.removeItem('lc:code');
		window.sessionStorage.removeItem('lc:totp_force_setup');

		// Post logout redirect.
		if (result.redirect && String(result.redirect.url).length) {
			window.location.assign(result.redirect.url);
			return;
		}
		window.sessionStorage.removeItem('serverWarnings');
		window.sessionStorage.removeItem('filters');
		window.sessionStorage.removeItem('application_settings');
		window.localStorage.removeItem('fb5f1c64-f438-4039-be25-54b004258b16-channelUserId');
		window.localStorage.removeItem('fb5f1c64-f438-4039-be25-54b004258b16-jwt');
		router.push('/auth/login');
	};
</script>

<style lang="scss" scoped>
	@use '../assets/styles/base' as base;
	@use '../assets/styles/modules/button';

	.user-icon-container {
		position: relative;
		width: auto;

		.user-menu {
			position: absolute;
			top: 58px;
			right: 9px;
			width: 150px;
			border-radius: 10px 6px 10px 10px;
			background-color: white;
			@include base.prefix(box-shadow, 0 3px 5px 0 rgba(base.$sidebar-create-color, 0.3));

			&::after {
				content: '';
				width: 0;
				height: 0;
				border-left: 8px solid transparent;
				border-right: 8px solid transparent;
				border-bottom: 8px solid white;
				position: absolute;
				top: -8px;
				right: 5px;
			}

			.user-menu-item {
				padding: 8px;
				/* border-bottom: 1px solid #e3e3e3; */
				/* width: 100%; */
				display: block;
				text-align: left;
				margin: 5px;
				width: calc(100% - 10px);
				&:last-child {
					border-bottom: none;
				}

				&:hover {
					background-color: #eff1f3;
					color: base.$sidebar-controller-color;
				}
			}
		}
	}

	.user-icon {
		height: 35px;
		width: 35px;
		background-color: base.$color_netcall-red;
		border: 2px solid base.$color_netcall-red;
		border-radius: 50%;
		padding-top: 8px;
		font-size: 14px;
		font-weight: bold;
		color: white;
		letter-spacing: 0px;
		text-align: center;
		cursor: pointer;
		@include base.prefix(transition, all 0.2s ease-in-out);

		&:hover {
			background-color: transparent;
			color: base.$color_netcall-red;
		}
	}
</style>
